import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, MoreVertical, AlertTriangle, FolderPlus, Search } from 'lucide-react';
import { useStore } from '@tanstack/react-store';
import { selectedLanguageStore, setSelectedLanguage } from '../../../../stores/selectedLanguage';
import { useListLanguages } from '../../../../hooks/useLanguages';
import { useGetFaqQuestions, useCreateFaqQuestion, useUpdateFaqQuestionStatus, useUpdateFaqQuestion, useDeleteFaqQuestion } from '../../../../hooks/useCommon';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export default function FAQsPage() {
    const { selectedLanguage } = useStore(selectedLanguageStore)
    const { cat_id } = useParams()
    const [languages, setLanguages] = useState([])
    const [questions, setQuestions] = useState([])
    const queryClient = useQueryClient();
    const createFaqQuestionMutation = useCreateFaqQuestion()
    const deleteFaqQuestionMutation = useDeleteFaqQuestion()
    const updateFaqQuestionMutation = useUpdateFaqQuestion()
    const updateStatusMutation = useUpdateFaqQuestionStatus()
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(null);
    const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
    const [newQuestion, setNewQuestion] = useState('');
    const [newAnswer, setNewAnswer] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
    const [deletingQuestion, setDeletingQuestion] = useState(null);
    const [showDeleteQuestionConfirmation, setShowDeleteQuestionConfirmation] = useState(false);
    const { data: languagesListResponse, isSuccess: isSuccessLanguagesList } = useListLanguages('')
    const { data: faqQuestionsResponse, isSuccess: isSuccessFaqQuestions } = useGetFaqQuestions(`?category_id=${cat_id}&language=${selectedLanguage}&search_key=${searchTerm}`)

    // Pagination functions
    let totalPages = Math.ceil(questions?.filter(question => question?.title?.toLowerCase().includes(searchTerm?.toLowerCase()))?.length / rowsPerPage)
    totalPages = totalPages == 0 ? 1 : totalPages

    const toggleDropdown = (id) => {
        setShowDropdown(showDropdown === id ? null : id);
    };

    const handleEditQuestion = () => {
        if (editingQuestion) {
            if (editingQuestion?.question?.trim() == "") {
                toast.error("Question is not allowed to empty")
                return false
            }
            if (editingQuestion?.answer?.trim() == "") {
                toast.error("Answer is not allowed to empty")
                return false
            }
            updateFaqQuestionMutation.mutate({
                question: editingQuestion?.question,
                answer: editingQuestion?.answer,
                language: selectedLanguage,
                _id: editingQuestion?._id
            }, {
                onSuccess: () => {
                    toast.success("Question Updated Successfully")
                    queryClient.invalidateQueries({ queryKey: ['faqs_questions'] });
                    setEditingQuestion(null);
                    setShowEditQuestionModal(false);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const handleDeleteQuestion = () => {
        if (deletingQuestion) {
            deleteFaqQuestionMutation.mutate(
                deletingQuestion?._id, {
                onSuccess: () => {
                    toast.success("Question has been deleted successfully")
                    queryClient.invalidateQueries({ queryKey: ['faqs_questions'] });
                    setShowDeleteQuestionConfirmation(false);
                    setDeletingQuestion(null);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const openEditQuestionModal = (question) => {
        setEditingQuestion({ ...question });
        setShowEditQuestionModal(true);
        setShowDropdown(null);
    };

    const openDeleteQuestionConfirmation = (question) => {
        setDeletingQuestion(question);
        setShowDeleteQuestionConfirmation(true);
        setShowDropdown(null);
    };

    const handleAddQuestion = () => {
        if (newQuestion == "") {
            toast.error("Question is not allowed to empty")
            return false
        }
        if(newAnswer == "") {
            toast.error("Answer is not allowed to empty")
            return false
        }
        createFaqQuestionMutation.mutate({
            question: newQuestion,
            answer: newAnswer,
            category_id: cat_id,
            language: selectedLanguage
        }, {
            onSuccess: () => {
                toast.success("New Question added Successfully")
                queryClient.invalidateQueries({ queryKey: ['faqs_questions'] });
                setNewQuestion('');
                setNewAnswer('');
                setShowAddQuestionModal(false)
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const toggleStatus = (id, currentStatus) => {
        updateStatusMutation.mutate({
            status: currentStatus == 1 ? 3 : 1,
            _id: id
        }, {
            onSuccess: () => {
                toast.success("Question status updated.")
                queryClient.invalidateQueries({ queryKey: ['faqs_questions'] });
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    useEffect(() => {
        if (isSuccessFaqQuestions) {
            setQuestions(faqQuestionsResponse?.data?.data?.result)
        }
    }, [faqQuestionsResponse, isSuccessFaqQuestions])

    useEffect(() => {
        if (isSuccessLanguagesList) {
            setLanguages(languagesListResponse?.data?.data?.result)
        }
    }, [languagesListResponse, isSuccessLanguagesList])

    return (
        <div className="flex h-screen bg-white text-gray-800">
            <div className="flex-1 flex flex-col">
                {/* Breadcrumb */}
                <div className="border-b border-gray-200 p-4 flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Dashboard</span>
                    <ChevronRight size={14} className="text-gray-400" />
                    <span className="text-gray-500">Settings</span>
                    <ChevronRight size={14} className="text-gray-400" />
                    <span>FAQ's</span>
                </div>
                {/* Content */}
                <div className="p-6 flex justify-between">
                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold mb-2">FAQ's</h1>
                        <p className="text-gray-500">Add frequently asked questions for users to see on your app and the website.</p>
                    </div>
                    <div className="mb-6 mt-4">
                        <button
                            onClick={() => setShowAddQuestionModal(true)}
                            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 flex items-center gap-2">
                            <FolderPlus size={16} />
                            Add New Question
                        </button>
                    </div>
                </div>
                <div className="mb-6 flex space-x-2">
                    {languages.map(language => (
                        <button
                            key={language?.short_code}
                            className={`px-4 py-2 rounded ${selectedLanguage === language?.short_code ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-600'}`}
                            onClick={() => setSelectedLanguage(language?.short_code)}>
                            {language?.name}
                        </button>
                    ))}
                </div>
                {/* Search */}
                <div className="mb-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by question title"
                            className="w-full p-2 pl-10 border border-gray-300 rounded"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                    </div>
                </div>
                {/* Categories Table */}
                <div className="border border-gray-200 rounded mb-4">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-4 py-3 text-left w-10">S.No</th>
                                <th className="px-4 py-3 text-left">Question</th>
                                <th className="px-4 py-3 text-left">Answer</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((question, i) => {
                                return (
                                    <tr key={question?._id} className="border-b border-gray-200">
                                        <td className="px-4 py-3">{i + 1}</td>
                                        <td
                                            className="px-4 py-3 flex items-center cursor-pointer">
                                            {question?.question}
                                        </td>
                                        <td 
                                            className="px-4 py-3 cursor-pointer">
                                            {question?.answer}
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <button
                                                onClick={() => toggleStatus(question?._id, question?.status)}
                                                className="focus:outline-none">
                                                <div className={`w-12 h-6 rounded-full flex items-center ${question?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                    <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${question.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                </div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="relative inline-block">
                                                <button
                                                    onClick={() => toggleDropdown(question?._id)}
                                                    className="p-1 hover:bg-gray-100 rounded-full">
                                                    <MoreVertical size={16} />
                                                </button>
                                                {showDropdown === question?._id && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-gray-200 z-10">
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => openEditQuestionModal(question)}
                                                                className="px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => openDeleteQuestionConfirmation(question)}
                                                                className="px-4 py-2 text-sm w-full text-left hover:bg-gray-100 flex items-center gap-2">
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div className="flex justify-end">
                    <div className="flex items-center">
                        <div className="mr-4 flex justify-between">
                            <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                            <select
                                className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="flex items-center text-sm">
                            <span className="mr-4">Page {currentPage} of {totalPages}</span>
                            <div className="flex">
                                <button
                                    className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}>
                                    <ChevronsLeft size={16} />
                                </button>
                                <button
                                    className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}>
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}>
                                    <ChevronRight size={16} />
                                </button>
                                <button
                                    className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}>
                                    <ChevronsRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add Question Modal */}
            {showAddQuestionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Add Question</h2>
                            <p className="text-gray-500 text-sm mb-6">Create a new question under selected category</p>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Question</label>
                                    <input
                                        type="text"
                                        value={newQuestion}
                                        onChange={(e) => setNewQuestion(e.target.value)}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter question...."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Answer</label>
                                    <input
                                        type="text"
                                        value={newAnswer}
                                        onChange={(e) => setNewAnswer(e.target.value)}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter answer..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => setShowAddQuestionModal(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAddQuestion()}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Edit Question Modal */}
            {showEditQuestionModal && editingQuestion && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Edit Question</h2>
                            <p className="text-gray-500 text-sm mb-6">Update the Question and Answer</p>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Question</label>
                                    <input
                                        type="text"
                                        value={editingQuestion.question}
                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter question..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Answer</label>
                                    <input
                                        type="text"
                                        value={editingQuestion?.answer}
                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, answer: e.target.value })}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter answer..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => {
                                    setEditingQuestion(null);
                                    setShowEditQuestionModal(false);
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleEditQuestion}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Question Confirmation Modal */}
            {showDeleteQuestionConfirmation && deletingQuestion && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                                    <AlertTriangle size={20} />
                                </div>
                                <h2 className="text-xl font-semibold">Delete Question</h2>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete this question? This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => {
                                    setDeletingQuestion(null);
                                    setShowDeleteQuestionConfirmation(false);
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteQuestion}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}